import React from 'react';
import { getActivityStats } from '../../activities/activityTypes';
import {
  FIELD_KEYS,
  pick,
  DetailTopBar,
  DetailHero,
  WeatherPlaceholder,
} from './ActivityDetailPage';

// Espeleología-specific field keys (on top of the shared ones).
const ESPELEO_KEYS = {
  cavidadTipo: ['cavidadTipo', 'tipoCavidad', 'tipo', 'caveType'],
  caidaMax: ['caidaMax', 'maxDrop', 'caidaVerticalMax', 'pozoMax'],
  tiempo: ['tiempo', 'time', 'duracion'],
  topo: ['topo', 'topoScale', 'escalaTopo'],
};

/**
 * Espeleología detail page. Follows the v3 "06 · Espeleología" design: cota
 * belt, title, description and photo on the left, and a data panel (desnivel /
 * caída máx. / tiempo / nº pozos / dificultad) on the right. The vertical topo
 * (corte de pozos) is a croquis feature that isn't implemented yet, so it's not
 * shown. Any datum absent from the activity is omitted; weather is a placeholder.
 */
function EspeleologiaDetail({ activity }) {
  const values = activity.values || {};
  const description = pick(values, FIELD_KEYS.description);
  const gpx = pick(values, FIELD_KEYS.gpx);
  const cavidadTipo = pick(values, ESPELEO_KEYS.cavidadTipo);
  const topo = pick(values, ESPELEO_KEYS.topo);

  const stats = getActivityStats(activity);
  const desnivel = stats.find((s) => s.unit === 'm');
  const pozos = stats.find((s) => s.unit === 'Pozos');
  const dificultad = stats.find((s) => s.unit === 'Dificultad');
  const caidaMax = pick(values, ESPELEO_KEYS.caidaMax);
  const tiempo = pick(values, ESPELEO_KEYS.tiempo);

  const tiles = [
    desnivel?.present && { key: 'des', label: 'DESNIVEL', value: desnivel.value, unit: 'm' },
    caidaMax && { key: 'caida', label: 'CAÍDA VERTICAL MÁX.', value: caidaMax, unit: 'm' },
    tiempo && { key: 'time', label: 'TIEMPO', value: tiempo, unit: 'h' },
    pozos?.present && { key: 'pozos', label: 'Nº DE POZOS', value: pozos.value },
    dificultad?.present && { key: 'dif', label: 'DIFICULTAD CAVIDAD', value: dificultad.value, key_stat: true },
  ].filter(Boolean);

  const eyebrow = [
    cavidadTipo && String(cavidadTipo).toUpperCase(),
    pozos?.present && `${pozos.value} POZOS`,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="Detail">
      <DetailTopBar
        activity={activity}
        rightCell={topo && <span className="Detail__barCell Detail__barCell--gpx">TOPO {String(topo).toUpperCase()}</span>}
      />

      <div className="Detail__body">
        <div className="Detail__col">
          <div className="Detail__titleText">
            {eyebrow && <span className="Detail__eyebrow">{eyebrow}</span>}
            <h1 className="Detail__title Detail__title--onDark">{activity.name}</h1>
          </div>

          <DetailHero activity={activity} />

          {description && (
            <section className="Detail__section">
              {String(description)
                .split(/\n{2,}/)
                .map((para, i) => (
                  <p key={i} className="Detail__desc">{para}</p>
                ))}
            </section>
          )}
        </div>

        <div className="Detail__col">
          {/* Corte vertical / topo de pozos: croquis no implementado → nada. */}

          {tiles.length > 0 && (
            <div className="Detail__stats">
              {tiles.map(({ key, label, value, unit, key_stat }) => (
                <div key={key} className={`Detail__stat${key_stat ? ' Detail__stat--key' : ''}`}>
                  <span className="Detail__statLabel">{label}</span>
                  <span className="Detail__statVal">
                    {value}
                    {unit && <em className="Detail__statUnit"> {unit}</em>}
                  </span>
                </div>
              ))}
            </div>
          )}

          <WeatherPlaceholder />
        </div>
      </div>

      {gpx && (
        <a className="Detail__gpxFooter" href={gpx} download>
          <span className="Detail__gpxFooterText">
            <span>SIGUIENTE PASO</span>
            <strong>DESCARGAR GPX</strong>
          </span>
          <span className="Detail__gpxFooterIcon" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="butt">
              <path d="M12 4 L12 16 M6 11 L12 17 L18 11 M4 20 L20 20" />
            </svg>
          </span>
        </a>
      )}
    </div>
  );
}

export default EspeleologiaDetail;
